import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface MerchantOverview {
	totalOrders: number;
	paidOrders: number;
	paymentOrders: number;
	partiallyPaidOrders: number;
	totalVolumeUSD: string;
	uniqueCustomers: number;
	activePaymentLinks: number;
	ordersLast24h: number;
	ordersLast7d: number;
	ordersLast30d: number;
}

export interface VolumeDataPoint {
	date: string;
	volume: number;
}

export interface VolumeCurrencyBreakdown {
	currency: string;
	totalVolume: number;
	volumeChangePct: number;
	series: VolumeDataPoint[];
}

export interface VolumeAnalyticsPeriod {
	currentStart: string;
	currentEnd: string;
	previousStart: string;
	previousEnd: string;
}

export interface VolumeAnalyticsResponse {
	period: VolumeAnalyticsPeriod;
	currencies: VolumeCurrencyBreakdown[];
}

export interface NetworkVolumeBreakdown {
	network: string;
	totalVolume: number;
	orderCount: number;
	percentage: number;
}

export interface NetworkBreakdownResponse {
	networks: NetworkVolumeBreakdown[];
}

export interface CurrencyVolumeBreakdown {
	currency: string;
	totalVolume: number;
	orderCount: number;
	percentage: number;
}

export interface CurrencyBreakdownResponse {
	currencies: CurrencyVolumeBreakdown[];
}

export interface OrderStatusDataPoint {
	date: string;
	paid: number;
	payment: number;
	partiallyPaid: number;
	pending: number;
	expired: number;
	cancelled: number;
}

export interface OrderStatusAnalyticsResponse {
	period: VolumeAnalyticsPeriod;
	series: OrderStatusDataPoint[];
}

const merchantAnalyticsProvider = {
	async getOverview(merchantId: string): Promise<MerchantOverview> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/analytics/overview`),
		);
		return response.data;
	},

	async getVolumeAnalytics(
		merchantId: string,
		startDate: string,
		endDate: string,
	): Promise<VolumeAnalyticsResponse> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/analytics/volume`),
			{
				params: {
					start_date: startDate,
					end_date: endDate,
				},
			},
		);
		return response.data;
	},

	async getNetworkBreakdown(
		merchantId: string,
		startDate: string,
		endDate: string,
	): Promise<NetworkBreakdownResponse> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/analytics/network-breakdown`),
			{
				params: {
					start_date: startDate,
					end_date: endDate,
				},
			},
		);
		return response.data;
	},

	async getCurrencyBreakdown(
		merchantId: string,
		startDate: string,
		endDate: string,
	): Promise<CurrencyBreakdownResponse> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/analytics/currency-breakdown`),
			{
				params: {
					start_date: startDate,
					end_date: endDate,
				},
			},
		);
		return response.data;
	},

	async getOrderStatusAnalytics(
		merchantId: string,
		startDate: string,
		endDate: string,
	): Promise<OrderStatusAnalyticsResponse> {
		const response = await apiRequest.get(
			withApiPath(`/merchant/${merchantId}/analytics/order-status`),
			{
				params: {
					start_date: startDate,
					end_date: endDate,
				},
			},
		);
		return response.data;
	},
};

export default merchantAnalyticsProvider;
